/**
 * Store locator section (custom).
 *
 * Lists the pickup locations configured in Zapiet Pickup + Delivery and plots them on a Google map.
 * Zapiet is the source of truth: the addresses, phone numbers and opening hours all come from its
 * storefront API, so the merchant maintains a single list instead of duplicating it in the theme.
 *
 *   POST /v1.0/pickup/locations  {shoppingCart, page}  -> { locations, pagination, services }
 *
 * The whole list is fetched once and paginated through, then searching and sorting happen locally.
 * That is deliberate: Zapiet's own geo search has to be enabled account-side and returns one page at
 * a time, whereas a pastry shop has a handful of boutiques that comfortably fit in memory — and a
 * local search keeps working when the Google Geocoding API is not enabled on the key.
 *
 * The section degrades in layers: without an API key it is a plain list, without Zapiet locations it
 * shows its empty message, and a failed map load never takes the list down with it.
 */

const API_BASE = 'https://api.zapiet.com/v1.0';
const CACHE_PREFIX = 'custom-store-locator';
const CACHE_TTL = 15 * 60 * 1000;
const REQUEST_TIMEOUT = 8000;
const MAX_PAGES = 20;
const EARTH_RADIUS_KM = 6371;

/** Monday first, as weeks are read in Switzerland — this is also the order the hours are listed in. */
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** A desaturated map, used only when no Map ID is set — with one, styling belongs to the console. */
const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ saturation: -100 }] },
  { elementType: 'labels.text', stylers: [{ saturation: -100 }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] }
];

/**
 * Loads the Google Maps script once per page, whatever the number of locator sections. The first
 * section decides the key: two locators with different keys is a misconfiguration, not a use case.
 */
function loadGoogleMaps(apiKey, language) {
  window.customStoreLocatorMapsPromise ??= new Promise((resolve, reject) => {
    const callback = 'customStoreLocatorMapsReady';
    const parameters = new URLSearchParams({
      key: apiKey,
      libraries: 'marker,geocoding',
      loading: 'async',
      callback,
      language
    });

    window[callback] = () => resolve(window.google.maps);

    const script = document.createElement('script');

    script.src = `https://maps.googleapis.com/maps/api/js?${parameters}`;
    script.async = true;
    script.onerror = () => reject(new Error('The Google Maps script could not be loaded'));

    document.head.appendChild(script);
  });

  return window.customStoreLocatorMapsPromise;
}

/**
 * The coordinates of a location as numbers, or null when Zapiet has none for it — an address typed
 * without a pin is still worth listing, it simply cannot be plotted or ranked by distance.
 */
function coordinatesOf(location) {
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
    ? { lat, lng }
    : null;
}

/** Great-circle distance in kilometres, which is accurate enough to rank stores by proximity. */
function distanceBetween(from, to) {
  const toRadians = (value) => (value * Math.PI) / 180;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(deltaLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** The weekday key Zapiet uses for its opening hours, in the location's own timezone. */
function todayKeyIn(timezone) {
  try {
    const name = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: timezone }).format(
      new Date()
    );

    return name.toLowerCase();
  } catch {
    // getDay() counts from Sunday, the list from Monday.
    return WEEKDAYS[(new Date().getDay() + 6) % 7];
  }
}

function readCache(key) {
  try {
    const entry = JSON.parse(window.sessionStorage.getItem(key));

    return entry && Date.now() - entry.at < CACHE_TTL ? entry.value : null;
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }));
  } catch {
    // Private browsing, or a full quota. The section just loses its cache.
  }
}

class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.shop = this.dataset.shop;
    this.apiKey = this.dataset.mapApiKey ?? '';
    this.mapId = this.dataset.mapId ?? '';
    this.defaultMarkers = this.dataset.defaultMarkers !== 'false';
    this.markerColor = this.dataset.markerColor || '#000000';
    this.desaturateMap = this.dataset.desaturateMap === 'true';
    this.locale = document.documentElement.lang || 'fr';
    this.revealItems = this.readRevealItems();

    this.list = this.querySelector('[data-list]');
    this.itemTemplate = this.querySelector('[data-item-template]');
    this.hoursRowTemplate = this.querySelector('[data-hours-row-template]');
    this.infoTemplate = this.querySelector('[data-info-template]');
    this.mapElement = this.querySelector('[data-map]');
    this.statusElement = this.querySelector('[data-status]');
    this.searchInput = this.querySelector('[data-search-field] input');

    // Keyed by state rather than read off `dataset`, because a dashed state name has no camelCase
    // dataset equivalent to look up.
    this.messages = {
      empty: this.dataset.emptyText ?? '',
      error: this.dataset.errorText ?? '',
      'no-results': this.dataset.noResultsText ?? '',
      'geolocation-error': this.dataset.geolocationErrorText ?? ''
    };

    this.locations = [];
    this.entries = new Map();
    this.markers = new Map();
    this.origin = null;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelector('[data-search-form]')?.addEventListener('submit', this.onSearch, { signal });
    this.querySelector('[name="geolocate"]')?.addEventListener('click', this.onGeolocate, { signal });
    this.list?.addEventListener('click', this.onListClick, { signal });

    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /* -- Data ---------------------------------------------------------------------------------- */

  async load() {
    this.setState('loading');

    // The picked products are part of the key, so editing the setting is seen without waiting.
    const revealKey = this.revealItems.map((item) => item.variant_id).join(',');
    const cacheKey = `${CACHE_PREFIX}:${this.shop}:${revealKey}`;
    const cached = readCache(cacheKey);
    let locations = cached;

    if (!locations) {
      try {
        locations = await this.fetchLocations();
      } catch (error) {
        console.warn('[store-locator] location lookup failed', error);
        this.setState('error');
        return;
      }

      writeCache(cacheKey, locations);
    }

    this.locations = locations;

    if (locations.length === 0) {
      this.setState('empty');
      return;
    }

    this.render(locations);
    this.setupMap();
  }

  /** The one-item carts rendered by the section, or none when the JSON is missing or unreadable. */
  readRevealItems() {
    try {
      const items = JSON.parse(this.querySelector('[data-reveal-items]')?.textContent ?? '[]');

      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  /**
   * Zapiet only returns the locations where every product of the cart can be picked up, so the empty
   * cart misses a location restricted to "Specific products". Each product picked in the section is
   * looked up on its own and the answers are merged. A failed extra lookup costs that boutique only,
   * never the whole list.
   */
  async fetchLocations() {
    const [base, ...extras] = await Promise.allSettled([
      this.fetchAllLocations([]),
      ...this.revealItems.map((item) => this.fetchAllLocations([item]))
    ]);

    if (base.status === 'rejected') {
      throw base.reason;
    }

    if (extras.length === 0) {
      return base.value;
    }

    const byId = new Map(base.value.map((location) => [location.id, location]));

    extras.forEach((result) => {
      if (result.status === 'rejected') {
        console.warn('[store-locator] extra location lookup failed', result.reason);
        return;
      }

      result.value.forEach((location) => {
        if (!byId.has(location.id)) {
          byId.set(location.id, location);
        }
      });
    });

    // Zapiet lists the locations by name: the merged ones are slotted in the same order.
    return [...byId.values()].sort((a, b) =>
      (a.company_name ?? '').localeCompare(b.company_name ?? '', this.locale)
    );
  }

  /**
   * Zapiet answers one page at a time. The loop is bounded rather than trusting `last_page`, so a
   * malformed payload can never spin the browser.
   */
  async fetchAllLocations(shoppingCart) {
    const locations = [];

    for (let page = 1; page <= MAX_PAGES; page++) {
      const data = await this.request(page, shoppingCart);

      // The endpoint answers 200 with an `error` key for the expected "nothing configured" case,
      // so the status code alone is not enough to tell an empty list from a failure.
      if (data.error) {
        break;
      }

      locations.push(...(data.locations ?? []));

      const pagination = data.pagination ?? {};

      if (!pagination.last_page || pagination.current_page >= pagination.last_page) {
        break;
      }
    }

    return locations;
  }

  async request(page, shoppingCart) {
    const response = await fetch(
      `${API_BASE}/pickup/locations?shop=${encodeURIComponent(this.shop)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Never the visitor's cart: this section advertises the boutiques, it does not promise that
        // the current cart can be picked up in each of them.
        body: JSON.stringify({ shoppingCart, geoSearchQuery: '', page }),
        signal: AbortSignal.timeout?.(REQUEST_TIMEOUT)
      }
    );

    if (!response.ok) {
      throw new Error(`Zapiet pickup/locations responded with ${response.status}`);
    }

    return response.json();
  }

  /* -- Rendering ----------------------------------------------------------------------------- */

  render(locations) {
    this.list.replaceChildren();
    this.entries.clear();

    locations.forEach((location) => {
      const item = this.buildItem(location);

      this.entries.set(String(location.id), { location, item });
      this.list.appendChild(item);
    });

    this.setState(locations.length > 0 ? 'ready' : 'no-results');
  }

  buildItem(location) {
    const item = this.itemTemplate.content.firstElementChild.cloneNode(true);
    const field = (name) => item.querySelector(`[data-field="${name}"]`);

    item.dataset.locationId = location.id;

    this.fillSummary(item, location);

    const coordinates = coordinatesOf(location);

    if (this.origin && coordinates) {
      const distanceElement = field('distance');
      const distance = distanceBetween(this.origin, coordinates);

      distanceElement.textContent = `${distance.toFixed(distance < 10 ? 1 : 0)} km`;
      distanceElement.hidden = false;
    }

    this.fillHours(field('hours'), location);

    // Authored by the merchant in the Zapiet admin, and rendered as markup there too, so it is
    // trusted the same way a theme setting is.
    const more = location.more_information?.trim();

    if (more) {
      const moreElement = field('more');

      moreElement.innerHTML = more;
      moreElement.hidden = false;
    }

    return item;
  }

  /**
   * The fields a list item and a map popup have in common. Each template holds only the fields it
   * shows, so a missing one is skipped rather than assumed.
   */
  fillSummary(root, location) {
    const field = (name) => root.querySelector(`[data-field="${name}"]`);

    field('name').textContent = location.company_name ?? '';
    field('address').textContent = this.formatAddress(location);

    const phone = location.phone?.trim();

    if (phone && field('phone')) {
      const phoneLink = field('phone');

      phoneLink.textContent = phone;
      phoneLink.href = `tel:${phone.replace(/\s+/g, '')}`;
      field('phone-row').hidden = false;
    }

    const today = this.formatToday(location);

    if (today && field('today')) {
      const todayElement = field('today');

      todayElement.textContent = today;
      todayElement.hidden = false;
    }

    const directions = field('directions');

    if (directions) {
      directions.href = this.directionsUrl(location);
    }
  }

  formatAddress(location) {
    return [
      location.address_line_1,
      location.address_line_2,
      [location.postal_code, location.city].filter(Boolean).join(' '),
      this.countryName(location)
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Zapiet stores the country name in English only ("Switzerland"), so the ISO code it sends along
   * is named in the page's language instead, falling back to Zapiet's name for an unknown code.
   */
  countryName(location) {
    try {
      return new Intl.DisplayNames([this.locale], { type: 'region' }).of(location.country_code);
    } catch {
      return location.country;
    }
  }

  /**
   * The opening hours are keyed by weekday name, and each day carries `opens`, `closes` and a
   * `closed` flag. A day with the flag on has meaningless times, so it is shown as closed instead.
   */
  fillHours(body, location) {
    const hours = location.opening_hours;

    if (!hours) {
      body.closest('table').hidden = true;
      return;
    }

    const today = todayKeyIn(location.timezone);
    const formatter = new Intl.DateTimeFormat(this.locale, { weekday: 'long' });

    WEEKDAYS.forEach((day, index) => {
      const entry = hours[day];

      if (!entry) {
        return;
      }

      const row = this.hoursRowTemplate.content.firstElementChild.cloneNode(true);

      // 2024-01-08 was a Monday, which lines the index up with the weekday order used above.
      row.querySelector('[data-field="day"]').textContent = formatter.format(
        new Date(Date.UTC(2024, 0, 8 + index))
      );
      row.querySelector('[data-field="range"]').textContent = this.formatRange(entry);
      row.classList.toggle('is-today', day === today);

      body.appendChild(row);
    });
  }

  formatRange(entry) {
    if (entry.closed || !entry.opens || !entry.closes) {
      return this.dataset.closedText ?? '';
    }

    return `${entry.opens} - ${entry.closes}`;
  }

  formatToday(location) {
    const entry = location.opening_hours?.[todayKeyIn(location.timezone)];

    if (!entry) {
      return '';
    }

    return `${this.dataset.todayLabel ?? ''} ${this.formatRange(entry)}`.trim();
  }

  directionsUrl(location) {
    const position = coordinatesOf(location);
    const destination = position
      ? `${position.lat},${position.lng}`
      : `${location.company_name} ${this.formatAddress(location)}`;

    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  }

  setState(state) {
    this.dataset.state = state;

    if (this.statusElement) {
      this.statusElement.hidden = state === 'ready' || state === 'loading';
      this.statusElement.textContent = this.messages[state] ?? '';
    }
  }

  /* -- Search -------------------------------------------------------------------------------- */

  onSearch = async (event) => {
    event.preventDefault();

    const query = this.searchInput?.value.trim() ?? '';

    if (query === '') {
      this.origin = null;
      this.apply(this.locations);
      return;
    }

    // A plain text match on the city, the postcode or the store name answers most searches, and it
    // needs nothing beyond the data already in hand. Geocoding is the fallback, not the first move.
    const matches = this.locations.filter((location) => this.matchesQuery(location, query));

    if (matches.length > 0) {
      this.origin = null;
      this.apply(matches);
      return;
    }

    const point = await this.geocode(query);

    if (!point) {
      this.origin = null;
      this.apply([]);
      return;
    }

    this.origin = point;
    this.apply(this.sortByDistance(this.locations));
  };

  matchesQuery(location, query) {
    const haystack = [
      location.company_name,
      location.address_line_1,
      location.address_line_2,
      location.city,
      location.postal_code,
      location.region,
      location.country,
      this.countryName(location),
      location.country_code
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    return normalize(haystack).includes(normalize(query.toLowerCase()));
  }

  onGeolocate = () => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.origin = { lat: position.coords.latitude, lng: position.coords.longitude };

        if (this.searchInput) {
          this.searchInput.value = '';
        }

        this.apply(this.sortByDistance(this.locations));
      },
      (error) => {
        console.warn('[store-locator] geolocation refused', error);
        this.setState('geolocation-error');
      }
    );
  };

  sortByDistance(locations) {
    return [...locations].sort((a, b) => this.distanceOf(a) - this.distanceOf(b));
  }

  distanceOf(location) {
    const coordinates = coordinatesOf(location);

    return this.origin && coordinates
      ? distanceBetween(this.origin, coordinates)
      : Number.POSITIVE_INFINITY;
  }

  /** Geocoding is optional: the key may not have the API enabled, and that must not break search. */
  async geocode(query) {
    if (this.apiKey === '') {
      return null;
    }

    try {
      const maps = await loadGoogleMaps(this.apiKey, this.locale);

      this.geocoder ??= new maps.Geocoder();

      const { results } = await this.geocoder.geocode({ address: query });
      const location = results?.[0]?.geometry?.location;

      return location ? { lat: location.lat(), lng: location.lng() } : null;
    } catch (error) {
      console.warn('[store-locator] geocoding failed', error);
      return null;
    }
  }

  apply(locations) {
    this.render(locations);
    this.syncMarkers(locations);
  }

  /* -- Map ----------------------------------------------------------------------------------- */

  async setupMap() {
    if (this.apiKey === '' || !this.mapElement) {
      return;
    }

    try {
      const maps = await loadGoogleMaps(this.apiKey, this.locale);

      this.maps = maps;
      this.map = new maps.Map(this.mapElement, {
        zoom: 12,
        center: { lat: 46.8182, lng: 8.2275 },
        mapId: this.mapId || undefined,
        styles: this.mapId === '' && this.desaturateMap ? MAP_STYLES : undefined,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });

      // One popup for the whole map, as on Google Maps itself: opening a pin closes the previous one.
      this.infoWindow = new maps.InfoWindow({ maxWidth: 320 });

      this.locations.forEach((location) => {
        const marker = this.createMarker(location);

        if (marker) {
          this.markers.set(String(location.id), marker);
        }
      });

      this.syncMarkers(this.locations);
    } catch (error) {
      console.warn('[store-locator] map could not be initialised', error);
      this.mapElement.hidden = true;
    }
  }

  createMarker(location) {
    const position = coordinatesOf(location);

    if (!position) {
      return null;
    }

    const title = location.company_name ?? '';
    const select = () => this.select(String(location.id), { pan: false, source: 'map' });

    // Advanced markers need a Map ID to render at all, so the classic marker stays the fallback for
    // a shop that has only pasted an API key. Either way, leaving the look unset gives Google's own
    // red pin.
    if (this.mapId !== '' && this.maps.marker?.AdvancedMarkerElement) {
      const content = this.defaultMarkers
        ? undefined
        : new this.maps.marker.PinElement({
            background: this.markerColor,
            borderColor: this.markerColor,
            glyphColor: '#ffffff'
          }).element;

      const marker = new this.maps.marker.AdvancedMarkerElement({
        position,
        title,
        content,
        gmpClickable: true
      });

      marker.addListener('click', select);

      return marker;
    }

    const marker = new this.maps.Marker({
      position,
      title,
      icon: this.defaultMarkers
        ? undefined
        : {
            path: 'M12 0C7.03 0 3 4.03 3 9c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9z',
            fillColor: this.markerColor,
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 1.4,
            anchor: new this.maps.Point(12, 24)
          }
    });

    marker.addListener('click', select);

    return marker;
  }

  /** Keeps the pins in step with the filtered list, then frames whatever is left on screen. */
  syncMarkers(locations) {
    if (!this.map) {
      return;
    }

    const visible = new Set(locations.map((location) => String(location.id)));

    // The filter may have hidden the pin the popup hangs from.
    this.infoWindow?.close();

    this.markers.forEach((marker, id) => {
      this.setMarkerMap(marker, visible.has(id) ? this.map : null);
    });

    const bounds = new this.maps.LatLngBounds();
    let count = 0;

    locations.forEach((location) => {
      const position = coordinatesOf(location);

      if (position && this.markers.has(String(location.id))) {
        bounds.extend(position);
        count++;
      }
    });

    if (this.origin) {
      bounds.extend(this.origin);
      count++;
    }

    if (count === 0) {
      return;
    }

    if (count === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(15);
    } else {
      this.map.fitBounds(bounds, 48);
    }
  }

  setMarkerMap(marker, map) {
    if ('setMap' in marker) {
      marker.setMap(map);
    } else {
      marker.map = map;
    }
  }

  /* -- Interaction --------------------------------------------------------------------------- */

  onListClick = (event) => {
    const item = event.target.closest('[data-location-id]');

    if (!item) {
      return;
    }

    if (event.target.closest('[data-toggle]')) {
      this.toggleDetails(item);
      return;
    }

    if (!event.target.closest('a')) {
      this.select(item.dataset.locationId, { pan: true, source: 'list' });
    }
  };

  toggleDetails(item) {
    const toggle = item.querySelector('[data-toggle]');
    const details = item.querySelector('[data-details]');
    const expanded = toggle.getAttribute('aria-expanded') === 'true';

    toggle.setAttribute('aria-expanded', String(!expanded));
    details.hidden = expanded;
  }

  select(id, { pan, source }) {
    const entry = this.entries.get(id);

    if (!entry) {
      return;
    }

    this.entries.forEach(({ item }) => item.classList.toggle('is-active', item === entry.item));
    this.openInfoWindow(id, entry.location, { focus: source === 'map' });

    if (pan) {
      const position = coordinatesOf(entry.location);

      if (position && this.map) {
        this.map.panTo(position);
        this.map.setZoom(Math.max(this.map.getZoom(), 15));
      }
    } else if (source === 'map') {
      this.scrollListTo(entry.item);
    }
  }

  openInfoWindow(id, location, { focus }) {
    const marker = this.markers.get(id);

    if (!this.infoWindow || !marker || !this.infoTemplate) {
      return;
    }

    const content = this.infoTemplate.content.firstElementChild.cloneNode(true);

    this.fillSummary(content, location);

    // The name goes in the popup header, so it sits level with Google's close button.
    const name = content.querySelector('[data-field="name"]');

    this.infoWindow.setOptions({ headerContent: name });
    this.infoWindow.setContent(content);
    // Opened from the list, the focus stays on the list rather than jumping into the map.
    this.infoWindow.open({ anchor: marker, map: this.map, shouldFocus: focus });
  }

  /**
   * Scrolls the list column only. `scrollIntoView` would also move the page, which on mobile — where
   * the map sits above the list — pulls the popup the customer just opened out of view.
   */
  scrollListTo(item) {
    const column = item.closest('.store-locator__list-column');

    if (!column) {
      return;
    }

    const columnBox = column.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();

    if (itemBox.top < columnBox.top) {
      column.scrollBy({ top: itemBox.top - columnBox.top, behavior: 'smooth' });
    } else if (itemBox.bottom > columnBox.bottom) {
      column.scrollBy({ top: itemBox.bottom - columnBox.bottom, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
